import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Trash2, AlertTriangle, X, Check } from "lucide-react";
import { toast } from "sonner";

const SCOPES = [
  { key: "projects_cascade", label: "Proyectos (en cascada)",
    detail: "Borra proyectos + gastos, nómina, asistencia, contratos de destajo, pagos de destajo y movimientos de almacén." },
  { key: "expenses", label: "Gastos",
    detail: "Borra TODOS los gastos registrados (pendientes, aprobados y pagados)." },
  { key: "payroll", label: "Nómina, asistencia y empleados",
    detail: "Borra empleados, recibos de nómina y registros de asistencia." },
  { key: "pieceworkers", label: "Destajistas, contratos y pagos",
    detail: "Borra el directorio de destajistas, sus contratos y los pagos asociados." },
  { key: "inventory", label: "Almacenes, productos y movimientos",
    detail: "Borra el catálogo de productos, almacenes y todos los movimientos de inventario." },
  { key: "vendors", label: "Proveedores",
    detail: "Borra el directorio de proveedores." },
  { key: "notifications", label: "Notificaciones y suscripciones push",
    detail: "Limpia el histórico de notificaciones in-app y suscripciones a notificaciones push." },
  { key: "files", label: "Registros de archivos subidos",
    detail: "Quita las referencias a los archivos subidos (los blobs en object storage permanecen)." },
];

export default function CleanupModal({ onClose }) {
  const [stats, setStats] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => setStats({}));
  }, []);

  const toggle = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(SCOPES.map((s) => s.key)));
  const clear = () => setSelected(new Set());

  const canSubmit = selected.size > 0 && confirm === "LIMPIAR" && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cleanup", {
        confirm,
        scopes: Array.from(selected),
      });
      toast.success(`Limpieza OK: ${data.total_deleted} documentos eliminados`);
      onClose(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al limpiar");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(2,6,14,0.85)", backdropFilter: "blur(6px)" }}>
      <div data-testid="cleanup-modal" className="panel-strong w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col" style={{ boxShadow: "0 0 40px rgba(220,38,38,0.35)" }}>
        <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: "1px solid var(--border)" }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-sm flex items-center justify-center" style={{ background: "rgba(220,38,38,0.18)", border: "1px solid #dc2626" }}>
              <Trash2 className="w-4 h-4" style={{ color: "#fca5a5" }} />
            </div>
            <div>
              <div className="label-mono" style={{ color: "#dc2626" }}>// PELIGRO · ZONA ADMIN</div>
              <div className="font-display font-black text-slate-100 tracking-wider">Limpiar datos</div>
            </div>
          </div>
          <button data-testid="cleanup-close-btn" onClick={() => onClose(false)} className="text-slate-500 hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="flex-1 overflow-y-auto p-5 space-y-4">
          <div className="px-3 py-2 rounded-sm flex items-start gap-2 text-xs" style={{ background: "rgba(220,38,38,0.1)", border: "1px solid #dc2626", color: "#fca5a5" }}>
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              Esta acción <strong>NO se puede deshacer</strong>. Los <strong>usuarios activos no se tocan</strong> — seguirás pudiendo iniciar sesión después.
              Considera primero descargar un reporte Excel de las obras que quieras conservar.
            </div>
          </div>

          <div className="flex items-center justify-between">
            <span className="label-mono" style={{ color: "var(--cyan)" }}>// SELECCIONA ÁREAS A LIMPIAR</span>
            <div className="flex gap-2">
              <button type="button" onClick={selectAll} className="text-[10px] uppercase tracking-widest font-semibold hover:underline" style={{ color: "var(--magenta)" }}>Todo</button>
              <button type="button" onClick={clear} className="text-[10px] uppercase tracking-widest font-semibold hover:underline" style={{ color: "var(--text-dim)" }}>Ninguno</button>
            </div>
          </div>

          {/* Quick action: dedupe expenses */}
          <button
            type="button"
            data-testid="dedupe-expenses-btn"
            onClick={async () => {
              if (!window.confirm("Esto eliminará gastos duplicados (mismo proyecto + fecha + folio + monto + descripción). Conserva el más antiguo de cada grupo. ¿Continuar?")) return;
              setBusy(true);
              try {
                const { data } = await api.post("/admin/dedupe-expenses");
                toast.success(`${data.deleted_count} duplicados eliminados (${data.duplicate_groups} grupos)`);
              } catch (e) {
                toast.error(e.response?.data?.detail || "Error al deduplicar");
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className="w-full px-3 py-2 rounded-sm text-xs uppercase tracking-widest font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
            style={{ background: "rgba(245,158,11,0.15)", border: "1px solid var(--amber)", color: "#fbbf24" }}
          >
            <AlertTriangle className="w-3.5 h-3.5" /> Quitar gastos duplicados (offline-queue legacy)
          </button>

          <div className="space-y-2">
            {SCOPES.map((s) => {
              const count = stats?.[s.key] ?? "…";
              const isSelected = selected.has(s.key);
              const disabled = stats && stats[s.key] === 0;
              return (
                <label
                  key={s.key}
                  data-testid={`cleanup-scope-${s.key}`}
                  className={`flex items-start gap-3 p-3 rounded-sm cursor-pointer transition-all ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  style={{
                    background: isSelected ? "rgba(220,38,38,0.08)" : "var(--bg-2)",
                    border: `1px solid ${isSelected ? "#dc2626" : "var(--border)"}`,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => toggle(s.key)}
                    className="mt-0.5"
                    style={{ accentColor: "#dc2626" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-slate-100">{s.label}</span>
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm" style={{ background: "var(--bg)", color: count > 0 ? "var(--cyan)" : "var(--text-dim)" }}>
                        {count} doc{count === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{s.detail}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {stats && (
            <div className="text-[11px] text-slate-500 label-mono border-t pt-2" style={{ borderColor: "var(--border)" }}>
              // Usuarios en sistema (SE CONSERVAN): <span className="text-slate-300">{stats.users_total}</span>
            </div>
          )}

          <div className="pt-3" style={{ borderTop: "1px solid var(--border)" }}>
            <label className="label-mono block mb-1.5">Escribe <span style={{ color: "#dc2626" }}>LIMPIAR</span> para confirmar</label>
            <input
              data-testid="cleanup-confirm-input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="LIMPIAR"
              className="w-full input-cyber px-3 py-2 text-sm font-mono tracking-widest"
              style={{ borderColor: confirm === "LIMPIAR" ? "var(--green)" : "var(--border)" }}
            />
          </div>
        </form>

        <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: "1px solid var(--border)" }}>
          <button type="button" onClick={() => onClose(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
          <button
            type="submit"
            data-testid="cleanup-submit-btn"
            onClick={submit}
            disabled={!canSubmit}
            className="px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: canSubmit ? "linear-gradient(90deg, #dc2626, #991b1b)" : "rgba(220,38,38,0.2)",
              border: "1px solid #dc2626",
              color: "#fff",
              boxShadow: canSubmit ? "0 0 15px rgba(220,38,38,0.5)" : "none",
            }}
          >
            {busy ? "Limpiando…" : <><Check className="w-4 h-4" /> Limpiar {selected.size} área(s)</>}
          </button>
        </div>
      </div>
    </div>
  );
}
